import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { agent } from '../api/agent';
import { Concept, Synonym, useSessionStore, sessionSelectors } from '../store/sessionStore';

const toKey = (label: string) => label.toLowerCase().trim();

const buildConceptSynonyms = (concepts: Concept[], synonyms: Synonym[]) =>
  concepts.map((concept) => ({
    concept,
    synonyms: synonyms.filter((synonym) => synonym.conceptId === concept.id && !synonym.dismissed)
  }));

export const SynonymMatrix = () => {
  const concepts = useSessionStore(sessionSelectors.concepts);
  const synonyms = useSessionStore(sessionSelectors.synonyms);
  const selectedStyles = useSessionStore(sessionSelectors.selectedStyles);
  const voteSynonym = useSessionStore((state) => state.voteSynonym);
  const dismissSynonym = useSessionStore((state) => state.dismissSynonym);
  const addSynonyms = useSessionStore((state) => state.addSynonyms);
  const removeConcept = useSessionStore((state) => state.removeConcept);

  const [pendingConceptId, setPendingConceptId] = useState<string | null>(null);

  const activeConcepts = useMemo(
    () => concepts.filter((concept) => concept.votes > 0),
    [concepts]
  );

  const grouped = useMemo(
    () => buildConceptSynonyms(activeConcepts, synonyms),
    [activeConcepts, synonyms]
  );

  const requestedConcepts = useRef(new Set<string>());

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (concept: Concept) => agent.generateSynonyms(concept, selectedStyles),
    onMutate: (concept) => {
      setPendingConceptId(concept.id);
      requestedConcepts.current.add(concept.id);
    },
    onError: (_error, concept) => {
      setPendingConceptId(null);
      if (concept) {
        requestedConcepts.current.delete(concept.id);
      }
    },
    onSuccess: (data, concept) => {
      const state = useSessionStore.getState();
      const existing = new Set(
        state.projects[state.activeProjectId]?.synonyms
          .filter((synonym) => synonym.conceptId === concept.id && !synonym.dismissed)
          .map((synonym) => toKey(synonym.label)) ?? []
      );

      const fresh = data.synonyms.filter((synonym) => !existing.has(toKey(synonym.label)));
      if (fresh.length > 0) {
        addSynonyms(fresh);
      }
      requestedConcepts.current.delete(concept.id);
      setPendingConceptId(null);
    }
  });

  useEffect(() => {
    const next = activeConcepts.find((concept) => {
      if (requestedConcepts.current.has(concept.id)) {
        return false;
      }
      const hasSynonyms = synonyms.some(
        (synonym) => synonym.conceptId === concept.id && !synonym.dismissed
      );
      return !hasSynonyms;
    });

    if (next && !isPending) {
      void mutateAsync(next);
    }
  }, [activeConcepts, synonyms, isPending, mutateAsync]);

  const handleGenerateAll = async () => {
    for (const concept of activeConcepts) {
      await mutateAsync(concept);
    }
  };

  if (activeConcepts.length === 0) {
    return (
      <div className="panel-section">
        <header className="section-header">
          <div>
            <h2>Synonym Explorer</h2>
          </div>
        </header>
        <p className="empty-state">Give a concept an upvote to unlock synonyms.</p>
      </div>
    );
  }

  return (
    <div className="panel-section">
      <header className="section-header">
        <div>
          <h2>Synonym Explorer</h2>
        </div>
        <button
          type="button"
          className="icon-button"
          onClick={handleGenerateAll}
          disabled={isPending || activeConcepts.length === 0}
          title="Generate synonyms"
        >
          {isPending ? '…' : '✚✨'}
        </button>
      </header>
      <div className="synonym-matrix">
        {grouped.map(({ concept, synonyms: items }) => {
          const isConceptPending = pendingConceptId === concept.id && isPending;

          return (
            <div key={concept.id} className="synonym-row">
              <div className="synonym-concept">
                <strong>{concept.label}</strong>
                <span>{concept.votes} votes</span>
                <div className="synonym-concept-actions">
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => mutateAsync(concept)}
                    disabled={isConceptPending}
                    title={`Generate synonyms for ${concept.label}`}
                  >
                    {isConceptPending ? '…' : '✚'}
                  </button>
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => removeConcept(concept.id)}
                    title={`Remove ${concept.label}`}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="synonym-list">
                {items.length === 0 ? (
                  <span className="empty-state">
                    {isConceptPending ? 'Generating synonyms…' : 'No synonyms yet.'}
                  </span>
                ) : (
                  items.map((synonym) => (
                    <div key={synonym.id} className="synonym-chip">
                      <span>{synonym.label}</span>
                      <span className="votes">{synonym.votes}</span>
                      <div className="chip-actions">
                        <button
                          type="button"
                          className="ghost-button"
                          title="Upvote synonym"
                          onClick={() => voteSynonym(synonym.id, 1)}
                        >
                          ▲
                        </button>
                        {/*<button*/}
                        {/*  type="button"*/}
                        {/*  className="ghost-button"*/}
                        {/*  title="Downvote synonym"*/}
                        {/*  onClick={() => voteSynonym(synonym.id, -1)}*/}
                        {/*  disabled={synonym.votes === 0}*/}
                        {/*>*/}
                        {/*  ▼*/}
                        {/*</button>*/}
                        <button
                          type="button"
                          className="ghost-button"
                          title="Dismiss synonym"
                          onClick={() => dismissSynonym(synonym.id)}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
